import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Link2, 
  ChevronUp, 
  ChevronDown, 
  Download, 
  Trash2,
  Zap,
  Brain,
  Plus
} from 'lucide-react';

/**
 * ChainStepItem - A single step in the chain builder
 * Displays step details, controls, and condition input
 */
const ChainStepItem = ({ 
  step, 
  index, 
  totalSteps,
  onMoveUp,
  onMoveDown,
  onRemove,
  onStopOnFailChange,
  onConditionChange
}) => {
  const { t } = useApiTestingLanguage();
  const getMethodBadgeColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-700';
      case 'POST': return 'bg-blue-100 text-blue-700';
      case 'PUT': return 'bg-yellow-100 text-yellow-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-white border rounded-lg p-3" data-testid={`chain-step-${index}`}>
      <div className="flex items-center gap-3">
        {/* Order controls */}
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === totalSteps - 1}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        {/* Step number */}
        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-sm">
          {index + 1}
        </div>

        {/* Step details */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge className={getMethodBadgeColor(step.test_details?.method)}>
              {step.test_details?.method || 'GET'}
            </Badge>
            <span className="font-medium">{step.test_details?.name || step.test_id.slice(0, 8)}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1 truncate">
            {step.test_details?.url || t("URL not available")}
          </div>
          {step.test_details?.extractions?.length > 0 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
              <Download className="h-3 w-3" />
              {t("Extracts:")} {step.test_details.extractions.map(e => e.variable_name).join(', ')}
            </div>
          )}
        </div>

        {/* Step options */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={step.stop_on_fail ?? true}
              onChange={(e) => onStopOnFailChange(e.target.checked)}
              className="rounded"
            />
            {t("Stop on fail")}
          </label>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-500 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Conditional Execution Input */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1">
              <Zap className="h-3 w-3 text-amber-500" />
              {t("Run Condition (optional)")}
            </label>
            <Input
              value={step.condition || ''}
              onChange={(e) => onConditionChange(e.target.value)}
              placeholder="e.g., status_code == 200 && {{token}} != ''"
              className="text-sm h-8"
              data-testid={`chain-step-${index}-condition`}
            />
            <p className="text-xs text-gray-400 mt-1">
              {t("Use:")} <code className="bg-gray-100 px-1 rounded">status_code</code>, <code className="bg-gray-100 px-1 rounded">response_time</code>, <code className="bg-gray-100 px-1 rounded">{"{{var}}"}</code> {t("with operators (==, !=, >, <, &&, ||)")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * VariableFlowIndicator - Shows info about variable propagation
 */
const VariableFlowIndicator = () => {
  const { t } = useApiTestingLanguage();
  return (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
    <p className="text-sm text-blue-700">
      <strong>{t("Variable Flow:")}</strong> {t("Variables extracted from earlier steps will be available in later steps via")} <code className="bg-blue-100 px-1 rounded">{"{{variableName}}"}</code> {t("syntax.")}
    </p>
  </div>
);
};

/**
 * AISuggestionPanel - Suggests tests that can use extracted variables
 */
const AISuggestionPanel = ({ availableVars, suggestedTests, onAddTest }) => {
  const { t } = useApiTestingLanguage();
  if (availableVars.length === 0 || suggestedTests.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 mt-3">
      <div className="flex items-start gap-2">
        <Brain className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-purple-800 mb-2">
            {t("AI Suggestion: Tests that can use your extracted variables")}
          </p>
          <div className="space-y-2">
            {suggestedTests.slice(0, 3).map(test => {
              const usedVars = availableVars.filter(v => {
                const testStr = JSON.stringify({url: test.url, headers: test.headers, body: test.body});
                return testStr.includes(`{{${v.variable_name}}}`);
              });
              
              return (
                <div key={test.id} className="flex items-center justify-between bg-white rounded p-2 border">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{test.method}</Badge>
                    <span className="text-sm font-medium">{test.name}</span>
                    <span className="text-xs text-purple-600">
                      {t("uses")} {usedVars.map(v => `{{${v.variable_name}}}`).join(', ')}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAddTest(test.id)}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t("Add")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TestSelector - Grid of available tests to add to chain
 */
const TestSelector = ({ tests, existingStepIds, onAddTest, getMethodColor }) => {
  const { t } = useApiTestingLanguage();
  return (
  <div className="border-t pt-3">
    <label className="text-sm font-medium text-gray-700 block mb-2">{t("Add Test to Chain:")}</label>
    <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
      {tests.filter(t => !existingStepIds.includes(t.id)).map((test) => (
        <button
          key={test.id}
          type="button"
          onClick={() => onAddTest(test.id)}
          className="flex items-start gap-2 p-2 border rounded hover:bg-gray-50 text-left"
        >
          <Badge className={getMethodColor(test.method)} variant="outline">
            {test.method}
          </Badge>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{test.name}</div>
            <div className="text-xs text-gray-500 truncate">{test.url}</div>
            {test.extractions?.length > 0 && (
              <div className="text-xs text-green-600">
                +{test.extractions.length} {t("extractions")}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  </div>
);
};

/**
 * ChainBuilderContent - The form content for creating/editing chains
 * 
 * This component handles the UI rendering while state management 
 * remains in the parent component (APITesting.js)
 * 
 * @param {Object} chainForm - The chain form data
 * @param {Function} onFormChange - Callback to update form data
 * @param {Array} environments - Available environments
 * @param {Array} apiTests - Available API tests
 * @param {Function} onAddStep - Callback to add a test as step
 * @param {Function} onRemoveStep - Callback to remove a step
 * @param {Function} onMoveStep - Callback to reorder steps
 * @param {Function} getMethodColor - Helper to get method badge color
 */
export const ChainBuilderContent = ({
  chainForm,
  onFormChange,
  environments,
  apiTests,
  onAddStep,
  onRemoveStep,
  onMoveStep,
  getMethodColor
}) => {
  const { t } = useApiTestingLanguage();
  // Calculate available variables from all steps
  const availableVars = chainForm.steps.flatMap(
    (s, idx) => (s.test_details?.extractions || []).map(e => ({
      ...e,
      fromStep: idx + 1,
      stepName: s.test_details?.name || `Step ${idx + 1}`
    }))
  );
  
  // Find tests that could use these variables
  const suggestedTests = apiTests.filter(test => {
    if (chainForm.steps.find(s => s.test_id === test.id)) return false;
    
    const testStr = JSON.stringify({
      url: test.url,
      headers: test.headers,
      body: test.body
    });
    
    return availableVars.some(v => 
      testStr.includes(`{{${v.variable_name}}}`)
    );
  });

  const existingStepIds = chainForm.steps.map(s => s.test_id);

  return (
    <div className="flex-1 overflow-y-auto space-y-4 py-4">
      {/* Chain Name & Description */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">{t("Chain Name *")}</label>
          <Input
            placeholder={t("User Authentication Flow")}
            value={chainForm.name}
            onChange={(e) => onFormChange({ ...chainForm, name: e.target.value })}
            data-testid="input-chain-name"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">{t("Environment (Optional)")}</label>
          <select
            value={chainForm.environment_id}
            onChange={(e) => onFormChange({ ...chainForm, environment_id: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="">{t("No Environment")}</option>
            {environments.map(env => (
              <option key={env.id} value={env.id}>{env.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">{t("Description")}</label>
        <Textarea
          placeholder={t("Describe what this chain tests...")}
          value={chainForm.description}
          onChange={(e) => onFormChange({ ...chainForm, description: e.target.value })}
          rows={2}
        />
      </div>

      {/* Steps Builder */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-900">{t("Chain Steps (")}{chainForm.steps.length})</h4>
        </div>

        {chainForm.steps.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Link2 className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>{t("No steps added yet. Select a test below to add it as a step.")}</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {chainForm.steps.map((step, index) => (
              <ChainStepItem
                key={index}
                step={step}
                index={index}
                totalSteps={chainForm.steps.length}
                onMoveUp={() => onMoveStep(index, 'up')}
                onMoveDown={() => onMoveStep(index, 'down')}
                onRemove={() => onRemoveStep(index)}
                onStopOnFailChange={(checked) => {
                  const newSteps = [...chainForm.steps];
                  newSteps[index].stop_on_fail = checked;
                  onFormChange({ ...chainForm, steps: newSteps });
                }}
                onConditionChange={(value) => {
                  const newSteps = [...chainForm.steps];
                  newSteps[index].condition = value;
                  onFormChange({ ...chainForm, steps: newSteps });
                }}
              />
            ))}

            {/* Variable flow indicator */}
            {chainForm.steps.length > 1 && <VariableFlowIndicator />}

            {/* AI Suggestion Panel */}
            {chainForm.steps.length > 0 && (
              <AISuggestionPanel
                availableVars={availableVars}
                suggestedTests={suggestedTests}
                onAddTest={onAddStep}
              />
            )}
          </div>
        )}

        {/* Add Step - Test Selector */}
        <TestSelector
          tests={apiTests}
          existingStepIds={existingStepIds}
          onAddTest={onAddStep}
          getMethodColor={getMethodColor}
        />
      </div>
    </div>
  );
};

export default ChainBuilderContent;
